import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  Box,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, gql } from '@apollo/client';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../config/firebase';
import logo from '../images/logo.png';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

// GraphQL queries and mutations
const VALIDATE_LOGIN_MAGIC_LINK = gql`
  query ValidateLoginMagicLink($token: String!) {
    validateLoginMagicLink(token: $token) {
      valid
      email
      error
    }
  }
`;

const CONSUME_LOGIN_MAGIC_LINK = gql`
  mutation ConsumeLoginMagicLink($token: String!) {
    consumeLoginMagicLink(token: $token) {
      valid
      firebaseToken
      email
      error
    }
  }
`;

type LoginState = 'loading' | 'valid' | 'invalid' | 'signing_in' | 'success' | 'error';

export default function LoginPage() {
  useDocumentTitle('Login');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [state, setState] = useState<LoginState>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  // Validate magic link on mount
  const { loading: validating } = useQuery(VALIDATE_LOGIN_MAGIC_LINK, {
    variables: { token: token || '' },
    skip: !token,
    onCompleted: (data) => {
      if (data.validateLoginMagicLink.valid) {
        setState('valid');
        setEmail(data.validateLoginMagicLink.email || '');
      } else {
        setState('invalid');
        setErrorMessage(data.validateLoginMagicLink.error || 'Invalid or expired link');
      }
    },
    onError: (error) => {
      setState('error');
      setErrorMessage(error.message || 'Failed to validate link');
    },
  });

  // Consume magic link mutation
  const [consumeLoginMagicLink] = useMutation(CONSUME_LOGIN_MAGIC_LINK);

  // Update state based on validation loading
  useEffect(() => {
    if (!token) {
      setState('invalid');
      setErrorMessage('No login token provided');
    } else if (validating) {
      setState('loading');
    }
  }, [token, validating]);

  const handleSignIn = async () => {
    if (!token) return;

    setState('signing_in');
    setErrorMessage('');

    try {
      const { data } = await consumeLoginMagicLink({ variables: { token } });

      if (!data.consumeLoginMagicLink.valid) {
        setState('error');
        setErrorMessage(data.consumeLoginMagicLink.error || 'Failed to sign in');
        return;
      }

      // Sign in with the custom token
      const firebaseToken = data.consumeLoginMagicLink.firebaseToken;
      if (!firebaseToken) {
        setState('error');
        setErrorMessage('No authentication token received');
        return;
      }

      await signInWithCustomToken(auth, firebaseToken);

      setState('success');

      // Check if there's a redirect path stored (from auth error redirect)
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');

      // Redirect after a short delay
      setTimeout(() => {
        if (redirectPath) {
          sessionStorage.removeItem('redirectAfterLogin');
          navigate(redirectPath);
        } else {
          navigate('/');
        }
      }, 1500);
    } catch (error: any) {
      console.error('Error signing in:', error);
      setState('error');
      setErrorMessage(error.message || 'Failed to sign in');
    }
  };

  // Render loading state
  if (state === 'loading') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>
            <Box sx={{ px: 2 }}>
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={80} sx={{ my: 2 }} />
              <Skeleton variant="text" height={30} />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render invalid/error state
  if (state === 'invalid' || state === 'error') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img
                src={logo}
                alt="Planapus Logo"
                style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
              />
            </Box>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                {state === 'invalid' ? 'Invalid Link' : 'Something Went Wrong'}
              </Typography>
            </Box>

            <Alert severity="error" sx={{ mb: 3 }}>
              {errorMessage}
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
              The sign-in link may have expired or already been used.
              Please request a new sign-in link.
            </Typography>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/auth')}
              >
                Back to Sign In
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render success state
  if (state === 'success') {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ py: 4 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Welcome back!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                You've successfully signed in.
              </Typography>
              <CircularProgress size={24} sx={{ mt: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Redirecting...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Render valid state (sign in preview)
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Card>
        <CardContent sx={{ py: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={logo}
              alt="Planapus Logo"
              style={{ height: '50px', width: 'auto', marginBottom: '16px' }}
            />
          </Box>

          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
            Sign In to Planapus
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mb: 3 }}>
            Click below to sign in as:
          </Typography>

          <Card variant="outlined" sx={{ mb: 4, bgcolor: 'grey.50' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EmailIcon sx={{ color: 'primary.main' }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {email}
              </Typography>
            </CardContent>
          </Card>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSignIn}
              disabled={state === 'signing_in'}
              sx={{ minWidth: 200 }}
            >
              {state === 'signing_in' ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            Not you? <Button size="small" onClick={() => navigate('/auth')}>Use a different email</Button>
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
}
